if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      abortController = undefined;
      onVariantChangeUnsubscriber = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get productId() {
        return this.getAttribute('data-product-id');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get quantityInput() {
        return this.querySelector('quantity-input input');
      }

      get productMediaWrapper() {
        return (this._productMediaWrapper = this._productMediaWrapper || this.querySelector('.product__media-wrapper'));
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this),
        );

        this.initQuantityHandlers();

        this.currentVariant = this.getSelectedVariant(this);
        if (this.currentVariant) {
          this.updateMedia(this.currentVariant);
          this.updateVariantMetafieldTitle(this.currentVariant.id);
        }

        this.renderDescriptionLists(this);
        this.renderFaqLists(this);
        this.ensureDefaultDescriptionTabOpen(this.getDescriptionContainerWithDetails());

        if (this.productMediaWrapper) {
          this.blockPositionHandler = this.handleBlocksPosition.bind(this);
          this.handleBlocksPosition();
          document.addEventListener('matchMobile', this.blockPositionHandler);
          document.addEventListener('unmatchMobile', this.blockPositionHandler);
        }
      }

      handleBlocksPosition() {
        const isMobile = FoxTheme.config.mqlMobile;
        const blocks = this.querySelectorAll(`.product__block[data-show-below-media="true"]`);

        blocks.forEach((block) => {
          const blockId = block.dataset.blockId;
          const blockPlacement = this.productMediaWrapper.querySelector(`[data-block-id="${blockId}"]`);
          if (!blockPlacement) return;

          if (isMobile) {
            const isMoveToMedia = block.hasAttribute('data-moved-below-media');
            if (isMoveToMedia) {
              block.innerHTML = blockPlacement.innerHTML;
              blockPlacement.innerHTML = '';
              block.removeAttribute('data-moved-below-media');
            }
          } else {
            blockPlacement.innerHTML = block.innerHTML;
            block.innerHTML = '';
            block.setAttribute('data-moved-below-media', '');
          }
        });
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
      }

      initializeProductSwapUtility() {
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues, resolvedVariantId } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        const viewMode = this.dataset.viewMode || 'main-product';

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage, resolvedVariantId),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage, viewMode)
            : this.handleUpdateProductInfo(productUrl, viewMode),
        });
      }

      get productForm() {
        return this.querySelector('form[is="product-form"');
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.resetFormState();
      }

      handleSwapProduct(productUrl, updateFullPage, viewMode) {
        return (html) => {
          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));

          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
            HTMLUpdateUtility.viewTransition(
              document.getElementById('shopify-section-sticky-atc-bar'),
              html.getElementById('shopify-section-sticky-atc-bar'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          }

          this.currentVariant = variant;
        };
      }

      handleUpdateProductInfo(productUrl, viewMode) {
        return (html) => {
          const variant = this.getSelectedVariant(html);
          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateShareUrl(variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.currentVariant = variant;
          this.updateMedia(variant);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
              return { source, destination };
            }

            return null;
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Barcode', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Badges', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('VariantMetafieldTitle', ({ innerText }) => innerText.trim() === '');
          const expandedDescriptionIds = this.getExpandedDetailsIds();
          const productDescriptionNodes = updateSourceFromDestination('ProductDescription');
          if (productDescriptionNodes) {
            const { source, destination } = productDescriptionNodes;
            if (source.dataset.currentVariantId) {
              destination.dataset.currentVariantId = source.dataset.currentVariantId;
            } else {
              destination.removeAttribute('data-current-variant-id');
            }
            this.restoreExpandedDetailsById(destination, expandedDescriptionIds);
          }

          this.renderDescriptionLists(this);
          this.renderFaqLists(this);

          if (this.productMediaWrapper) {
            this.handleBlocksPosition();
          }

          updateSourceFromDestination('PricePerItem', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Volume');
          this.updateVariantMetafieldTitle(variant.id);

          this.updateQuantityRules(this.sectionId, this.productId, html);
          this.querySelector(`#QuantityRules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#VolumeNote-${this.dataset.section}`)?.classList.remove('hidden');

          HTMLUpdateUtility.viewTransition(
            document.querySelector(`#SizeChart-${this.sectionId}`),
            html.querySelector(`#SizeChart-${this.sectionId}`),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks,
          );

          const addButtonUpdated = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          this.toggleAddButton(
            addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true,
            FoxTheme.variantStrings.soldOut,
          );

          document.dispatchEvent(
            new CustomEvent('variant:changed', {
              detail: {
                variant: this.currentVariant,
              },
            }),
          );

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false, resolvedVariantId = null) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (resolvedVariantId) {
          params.push(`variant=${resolvedVariantId}`);
        } else if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        if (selectedVariant) {
          return JSON.parse(selectedVariant);
        }

        const sectionId = productInfoNode.dataset?.section;
        const variantInput = productInfoNode.querySelector(
          `#product-form-${sectionId} input[name="id"], form[is="product-form"] input[name="id"]`,
        );
        if (!variantInput?.value) return null;

        const parsedVariantId = Number(variantInput.value);
        return {
          id: Number.isFinite(parsedVariantId) ? parsedVariantId : variantInput.value,
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch(() => { });
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      updateURL(url, variantId) {
        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      updateVariantInputs(variantId) {
        document
          .querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`)
          .forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
      }

      updateMedia(variant) {
        const productMedia = this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
        if (!productMedia) return; // Early return if productMedia is not found

        const setActiveMedia = () => {
          if (typeof productMedia.setActiveMedia === 'function') {
            productMedia.init();
            productMedia.setActiveMedia(variant);
            return true; // Indicate success
          }
          return false; // Indicate failure
        };

        if (!setActiveMedia()) {
          this.timer = setInterval(() => {
            if (setActiveMedia()) {
              clearInterval(this.timer);
            }
          }, 100);
        }
      }

      updateShareUrl(variantId) {
        if (!variantId) return;
        const shareButton = document.getElementById(`ProductShare-${this.dataset.section}`);
        if (!shareButton || !shareButton.updateUrl) return;
        shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${variantId}`);
      }

      getVariantMetafieldTitleData() {
        if (this.variantMetafieldTitleData) return this.variantMetafieldTitleData;

        const script = this.querySelector(`#VariantMetafieldTitleData-${this.dataset.section}`);
        if (!script?.textContent) {
          this.variantMetafieldTitleData = [];
          return this.variantMetafieldTitleData;
        }

        try {
          this.variantMetafieldTitleData = JSON.parse(script.textContent);
        } catch (e) {
          this.variantMetafieldTitleData = [];
        }

        return this.variantMetafieldTitleData;
      }

      updateVariantMetafieldTitle(variantId) {
        const target = this.querySelector(`#VariantMetafieldTitle-${this.dataset.section}`);
        if (!target) return;

        const vendor = target.dataset.spuVendor || '';
        const titleData = this.getVariantMetafieldTitleData().find(
          (item) => String(item.id) === String(variantId),
        );
        const variantTitle = titleData?.title || '';
        const combined = [vendor, variantTitle].filter(Boolean).join(' ');

        target.textContent = combined;
        target.classList.toggle('hidden', combined === '');
      }

      toggleAddButton(disable = true, text, modifyClass = true) {
        const productForm = document.getElementById(`product-form-${this.dataset.section}`);
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector('[name="add"] > span');
        const buyNowButton = this.querySelector('.aod_buynow.in_stock_buynow');
        if (!addButton) return;

        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = text;
          buyNowButton.style.display = "none";
        } else {
          addButton.removeAttribute('disabled');
          addButtonText.innerHTML = FoxTheme.variantStrings.addToCart;
          buyNowButton.style.display = "block";
        }

        if (!modifyClass) return;
      }

      setUnavailable() {
        this.toggleAddButton(true, FoxTheme.variantStrings.unavailable);
        const price = document.getElementById(`price-${this.dataset.section}`);
        const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
        const sku = document.getElementById(`Sku-${this.dataset.section}`);

        if (price) price.classList.add('hidden');
        if (inventory) inventory.classList.add('hidden');
        if (sku) sku.classList.add('hidden');
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.setQuantityBoundries();
        if (!this.hasAttribute('data-original-section')) {
          this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this),
          );
        }
      }

      setQuantityBoundries() {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityBoundries, {
          data: {
            sectionId: this.sectionId,
            productId: this.productId,
          },
        });
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.productIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart')?.classList.add('btn--loading');

        fetch(`${this.getAttribute('data-url')}?variant=${currentVariantId}&section_id=${this.sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.sectionId, this.productId, parsedHTML);
          })
          .catch(() => { })
          .finally(() => {
            this.querySelector('.quantity__rules-cart')?.classList.remove('btn--loading');
          });
      }

      updateQuantityRules(sectionId, productId, parsedHTML) {
        if (!this.quantityInput) return;

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityRules, {
          data: {
            sectionId,
            productId,
            parsedHTML,
          },
        });

        this.setQuantityBoundries();
      }

      parseDescriptionItems(rawValue) {
        if (typeof rawValue !== 'string') return [];

        let text = rawValue
          .replace(/[“”]/g, '"')
          .replace(/[‘’]/g, "'")
          .trim();

        const wrappedQuotes = [
          ["'", "'"],
          ['"', '"'],
        ];
        wrappedQuotes.forEach(([start, end]) => {
          if (text.startsWith(start) && text.endsWith(end)) {
            text = text.slice(1, -1).trim();
          }
        });


        if (!text) return [];

        if (text.startsWith('[') && text.endsWith(']')) {
          try {
            const parsed = JSON.parse(text);
            if (Array.isArray(parsed)) {
              const items = parsed.map((item) => this.sanitizeDescriptionItem(item)).filter(Boolean);
              return items;
            }
          } catch (error) {
            const inner = text.slice(1, -1);
            const quotedMatches = [...inner.matchAll(/"((?:\\.|[^"\\])*)"/g)]
              .map((match) => this.sanitizeDescriptionItem(match[1].replace(/\\"/g, '"')))
              .filter(Boolean);

            if (quotedMatches.length) {
              return quotedMatches;
            }

            const commaSplit = inner
              .split(/",\s*"/)
              .map((item) => this.sanitizeDescriptionItem(item.replace(/^"+|"+$/g, '').replace(/\\"/g, '"')))
              .filter(Boolean);
            if (commaSplit.length) {
              return commaSplit;
            }
          }
        }

        const newlineItems = text
          .split(/\r?\n/)
          .map((item) => this.sanitizeDescriptionItem(item))
          .filter(Boolean);
        return newlineItems;
      }

      sanitizeDescriptionItem(item) {
        return String(item).trim().replace(/^[-*•]\s+/, '');
      }

      renderDescriptionLists(rootNode) {
        if (!rootNode) return;

        const containers = rootNode.querySelectorAll('[data-description-items]');

        containers.forEach((container) => {
          const items = this.parseDescriptionItems(container.dataset.descriptionItems);
          if (!items.length) {
            return;
          }

          const list = document.createElement('ul');
          items.forEach((item) => {
            const listItem = document.createElement('li');
            listItem.textContent = item;
            list.appendChild(listItem);
          });

          container.replaceChildren(list);
        });
      }

      parseFaqItems(rawValue) {
        if (typeof rawValue !== 'string') return [];

        let text = rawValue
          .replace(/[“”]/g, '"')
          .replace(/[‘’]/g, "'")
          .trim();

        const wrappedQuotes = [
          ["'", "'"],
          ['"', '"'],
        ];
        wrappedQuotes.forEach(([start, end]) => {
          if (text.startsWith(start) && text.endsWith(end)) {
            text = text.slice(1, -1).trim();
          }
        });

        if (!(text.startsWith('[') && text.endsWith(']'))) return [];

        try {
          const parsed = JSON.parse(text);
          if (!Array.isArray(parsed)) return [];

          return parsed
            .map((item) => {
              if (!item || typeof item !== 'object') return null;
              const question = String(item.question ?? '').trim();
              const answer = String(item.answer ?? '').trim();
              if (!question && !answer) return null;
              return { question, answer };
            })
            .filter(Boolean);
        } catch (error) {
          const pairs = [];
          const pattern = /"question"\s*:\s*"((?:\\.|[^"\\])*)"\s*,\s*"answer"\s*:\s*"((?:\\.|[^"\\])*)"/g;
          let match = pattern.exec(text);

          while (match) {
            pairs.push({
              question: match[1].replace(/\\"/g, '"').trim(),
              answer: match[2].replace(/\\"/g, '"').trim(),
            });
            match = pattern.exec(text);
          }

          return pairs.filter((item) => item.question || item.answer);
        }
      }

      renderFaqLists(rootNode) {
        if (!rootNode) return;

        const containers = rootNode.querySelectorAll('[data-faq-items]');
        containers.forEach((container) => {
          const faqList = this.parseFaqItems(container.dataset.faqItems);
          if (!faqList.length) return;

          const list = document.createElement('div');
          list.className = 'faq-list';
          for (let i = 0; i < faqList.length; i += 1) {
            const item = faqList[i];
            const faqItem = document.createElement('p');
            const question = document.createElement('p');
            const answer = document.createElement('p');

            faqItem.className = 'faq-item';
            question.className = 'font-body-bolder';

            question.textContent = `Q: ${item.question}`;
            answer.textContent = `A: ${item.answer}`;

            faqItem.appendChild(question);
            faqItem.appendChild(answer);
            list.appendChild(faqItem);
          }

          container.replaceChildren(list);
        });
      }

      getExpandedDetailsIds() {
        const containers = this.querySelectorAll(`[data-block-id="description"]`);
        if (!containers.length) return [];

        const expandedIds = new Set();
        containers.forEach((container) => {
          container.querySelectorAll('details[open][id]').forEach((detailsEl) => {
            expandedIds.add(detailsEl.id);
          });
        });

        return [...expandedIds];
      }

      getDescriptionContainerWithDetails() {
        const containers = this.querySelectorAll('[data-block-id="description"]');
        for (const container of containers) {
          if (container.querySelector('details[id]')) return container;
        }

        return null;
      }

      restoreExpandedDetailsById(container, expandedIds) {
        if (!container) {
          return;
        }

        const allDetails = container.querySelectorAll('details[id]');

        if (!expandedIds?.length) {
          this.ensureDefaultDescriptionTabOpen(container);
          return;
        }

        const nextExpandedIds = new Set(expandedIds);
        allDetails.forEach((detailsEl) => {
          if (!nextExpandedIds.has(detailsEl.id)) return;
          detailsEl.open = true;
          detailsEl.setAttribute('aria-expanded', 'true');
        });
      }

      ensureDefaultDescriptionTabOpen(container) {
        if (!container) return;

        const firstDetails = container.querySelector('details[id]');
        if (!firstDetails) return;

        firstDetails.open = true;
        firstDetails.setAttribute('aria-expanded', 'true');
      }
    },
  );
}

if (!customElements.get('product-promotion-alert')) {
  customElements.define(
    'product-promotion-alert',
    class ProductPromotionAlert extends HTMLElement {
      constructor() {
        super();

        this.abortController = new AbortController();
        this.buttonEl = this.querySelector('button');
        this.buttonEl.addEventListener('click', this.onClick.bind(this), {
          signal: this.abortController.signal,
        });
      }

      onClick(evt) {
        evt.preventDefault();

        this.closest('.product__block').classList.add('hidden');
      }

      disconnectedCallback() {
        this.abortController.abort();
      }
    },
  );
}
